// Hunt map ground painter: ground, patches, water (or lava, ice, the Styx) and cliffs,
// painted once per map into a canvas in the same look as the creatures: five-step ramps
// with cool shadows, flat calm areas, a few clear details, dark lines only where something
// has an edge.
//
// Walls never move. The server walks on tile centres; here every pixel's class comes from
// the tile grid blurred into round shapes, nudged by the same noise fields that made the
// tiles, so coastlines and ridges curve naturally while every tile centre keeps its class.
//
// Colours live in LOOKS, keyed by biome id (shared/worldmap.ts BIOMES). A biome without a
// look falls back to its own four-step ramps.
import { sample, vnoise, tileRand, BIOMES, T_WATER, T_CLIFF, type WorldMap, type Biome } from '../shared/worldmap.ts';

const TILE = 24;

/** Dark to light: deep shadow, shadow, mid, light, highlight. */
type Ramp = readonly [string, string, string, string, string];
/** How plain ground is textured. */
type GroundStyle = 'grass' | 'snow' | 'sand' | 'rock';
/** dirt: rimmed, with pebbles. soft: dithered edge, no rim. rock: rimmed with cracks. paving: flagstones. */
type PatchStyle = 'dirt' | 'soft' | 'rock' | 'paving';

interface Look {
  ground: Ramp;
  style: GroundStyle;
  patch: Ramp;
  patchStyle: PatchStyle;
  liquid: Ramp;
  /** outline, dark face, face, light face, lip */
  cliff: Ramp;
  /** Plateau tops: the ground ramp (grass or snow up there) or bare rock. */
  top: 'ground' | 'rock';
  /** The bank where ground drops into the liquid: [lower, upper]. */
  bank: readonly [string, string];
}

// ---- shared ramps ----
const SNOW: Ramp = ['#5c6690', '#8f9cc6', '#b9c5e2', '#dae2f2', '#f6f9ff'];
/** Wind-scoured snow and blue ice showing through, soft-edged so it never reads as a hole. */
const SNOW_ROCK: Ramp = ['#56608a', '#8290bc', '#a6b4d8', '#c8d4ec', '#e8eef8'];
const FJORD_SEA: Ramp = ['#0a1c3a', '#143664', '#205e94', '#4c90c6', '#c0e8f8'];
const COLD_CLIFF: Ramp = ['#141628', '#2c3050', '#464c6e', '#6a7294', '#9aa4c4'];
const STONE_CLIFF: Ramp = ['#12121e', '#2a2a3a', '#424254', '#5e5e72', '#86869a'];
const LIMESTONE: Ramp = ['#2e221e', '#6a5444', '#9a8064', '#c4aa88', '#e8d8b8'];
const BASALT: Ramp = ['#08060a', '#1a1218', '#2c2026', '#423036', '#5e464a'];
const LAVA: Ramp = ['#3a0a08', '#8a1c0c', '#dc4a14', '#ff9628', '#ffe27a'];
const AEGEAN: Ramp = ['#0a2246', '#16447e', '#2472b2', '#4ea6d8', '#c4eeff'];
const DRY_PATH: Ramp = ['#3a2618', '#664830', '#8e6c48', '#b08e62', '#d2b484'];
const YOMI_STONE: Ramp = ['#0e1218', '#242c32', '#3a4648', '#56645e', '#7c8a7c'];

const LOOKS: Record<string, Look> = {
  // ---- Midgard: frost and firelight ----
  snow: { ground: SNOW, style: 'snow', patch: SNOW_ROCK, patchStyle: 'soft', liquid: ['#0c1e3e', '#173a68', '#2a6698', '#5c9ccc', '#c8ecfa'], cliff: COLD_CLIFF, top: 'ground', bank: ['#2a2e4a', '#4a5274'] },
  taiga: { ground: SNOW, style: 'snow', patch: ['#1a2426', '#2a3a36', '#3e5048', '#566a5a', '#76887a'], patchStyle: 'dirt', liquid: ['#0c1e3e', '#173a68', '#2a6698', '#5c9ccc', '#c8ecfa'], cliff: COLD_CLIFF, top: 'ground', bank: ['#2a2e4a', '#4a5274'] },
  moor: { ground: ['#121c1c', '#22342a', '#344a34', '#4a623e', '#6a7e50'], style: 'grass', patch: ['#1e1424', '#36243a', '#4e3650', '#684c66', '#8a6c82'], patchStyle: 'soft',
    liquid: ['#0e1c2a', '#1a3448', '#2a5068', '#467a90', '#9ccad8'], cliff: STONE_CLIFF, top: 'ground', bank: ['#1c1612', '#382a20'] },
  crag: { ground: ['#16161e', '#2c2a34', '#44404a', '#5e5862', '#7e7680'], style: 'rock', patch: ['#1e1612', '#34261e', '#4c382a', '#64503a', '#806a50'], patchStyle: 'dirt',
    liquid: ['#0a1830', '#143058', '#225482', '#4a84b0', '#b4dcf0'], cliff: ['#0e0e16', '#26242e', '#3c3a46', '#58546a', '#7c7890'], top: 'rock', bank: ['#1a1820', '#34303c'] },
  fjord: { ground: SNOW, style: 'snow', patch: SNOW_ROCK, patchStyle: 'soft', liquid: FJORD_SEA, cliff: COLD_CLIFF, top: 'ground', bank: ['#2a2e4a', '#4a5274'] },
  fields: { ground: ['#22220e', '#3e3e1c', '#5a5828', '#78743a', '#9c9452'], style: 'grass', patch: ['#24180e', '#3e2c1a', '#5a4228', '#765a3a', '#947652'], patchStyle: 'dirt',
    liquid: ['#0a1c34', '#14365c', '#215a88', '#468ab4', '#b0dcf0'], cliff: STONE_CLIFF, top: 'ground', bank: ['#2a1c10', '#4a3420'] },
  ember: { ground: ['#0c080e', '#1c141c', '#2e2228', '#423238', '#5c464c'], style: 'rock', patch: ['#18161a', '#2c282c', '#433d40', '#5c5456', '#7a7072'], patchStyle: 'dirt',
    liquid: LAVA, cliff: BASALT, top: 'rock', bank: ['#0a0608', '#1e1216'] },
  glacier: { ground: ['#384a78', '#6a80b0', '#9ab2d8', '#c6d8ee', '#eef6ff'], style: 'snow', patch: ['#3a5288', '#6a88bc', '#a4bee0', '#c8daf0', '#eaf4fe'], patchStyle: 'soft',
    liquid: ['#0c2448', '#1a4e86', '#3a8cc4', '#84c8ea', '#e6fbff'], cliff: ['#141a38', '#2e3c6a', '#4a5e94', '#7088bc', '#a8c0e4'], top: 'ground', bank: ['#1a2a58', '#2e4680'] },
  // ---- Hellas: marble and sun ----
  meadow: { ground: ['#1a2614', '#30441e', '#4a6228', '#687e36', '#90a04c'], style: 'grass', patch: DRY_PATH, patchStyle: 'dirt', liquid: AEGEAN, cliff: LIMESTONE, top: 'ground', bank: ['#3a2818', '#62462c'] },
  coast: { ground: ['#5c4636', '#9c8060', '#c4a87e', '#dcc496', '#f2e4be'], style: 'sand', patch: ['#1e3020', '#34482a', '#4c6436', '#687e44', '#8c9c5a'], patchStyle: 'soft',
    liquid: ['#0a2a52', '#16508c', '#2280be', '#56b6e0', '#d0f4ff'], cliff: LIMESTONE, top: 'ground', bank: ['#5a4430', '#7e6448'] },
  ash: { ground: ['#141216', '#28242a', '#3e383e', '#565056', '#746c70'], style: 'rock', patch: ['#0e0c10', '#1e1a20', '#302a30', '#463e44', '#605660'], patchStyle: 'dirt',
    liquid: LAVA, cliff: BASALT, top: 'rock', bank: ['#0a0608', '#1e1216'] },
  marsh: { ground: ['#101c14', '#1e3020', '#2e442a', '#425a34', '#5e7444'], style: 'grass', patch: ['#1a120e', '#30241a', '#463626', '#5e4a34', '#7a6448'], patchStyle: 'soft',
    liquid: ['#0e2222', '#183632', '#265048', '#3a6e5e', '#88b8a0'], cliff: STONE_CLIFF, top: 'ground', bank: ['#1e1610', '#3a2c1e'] },
  ruins: { ground: ['#2e2018', '#5a4430', '#80664a', '#a08460', '#c2a67e'], style: 'rock', patch: ['#3e3a4c', '#6c687e', '#9894a8', '#c0bccc', '#e6e4ee'], patchStyle: 'paving',
    liquid: AEGEAN, cliff: ['#2a2224', '#5a5058', '#847a82', '#aaa2a6', '#d0cac8'], top: 'rock', bank: ['#3a2818', '#5a4028'] },
  underworld: { ground: ['#100c1a', '#1e182c', '#2e263e', '#403654', '#5a4e70'], style: 'rock', patch: ['#1a1822', '#2c2838', '#40394e', '#584e66', '#766a84'], patchStyle: 'dirt',
    liquid: ['#100a24', '#1c1440', '#2c2060', '#4a3690', '#9a82e0'], cliff: ['#06040a', '#16101e', '#26203a', '#3a3050', '#56486c'], top: 'rock', bank: ['#140e20', '#2a2038'] },
  highland: { ground: ['#182214', '#2c3c22', '#44562e', '#5e703c', '#808e54'], style: 'grass', patch: DRY_PATH, patchStyle: 'dirt', liquid: AEGEAN, cliff: LIMESTONE, top: 'ground', bank: ['#3a2818', '#62462c'] },
  // ---- Yomi: lanterns at dusk ----
  bamboo: { ground: ['#0c1e1c', '#16342a', '#244c36', '#366644', '#52825a'], style: 'grass', patch: ['#22160e', '#3c2818', '#563c24', '#725434', '#90704a'], patchStyle: 'soft',
    liquid: ['#081c24', '#10364a', '#1a5a70', '#348c98', '#9ad8d8'], cliff: YOMI_STONE, top: 'ground', bank: ['#2a1c12', '#46301e'] },
  shrine: { ground: ['#10221c', '#1e3a2a', '#2e5238', '#426a46', '#60865a'], style: 'grass', patch: ['#26242c', '#46444e', '#66646e', '#8a8892', '#b4b2ba'], patchStyle: 'paving',
    liquid: ['#081c2a', '#123852', '#1e5c7c', '#3c8ca8', '#a4dce6'], cliff: YOMI_STONE, top: 'ground', bank: ['#2a1c12', '#46301e'] },
  river: { ground: ['#0e2018', '#1a3826', '#2a5232', '#3e6c3e', '#5c8852'], style: 'grass', patch: ['#1e1e26', '#363844', '#525462', '#727482', '#9a9ca8'], patchStyle: 'dirt',
    liquid: ['#08202a', '#103e4c', '#1a626c', '#349290', '#a0e0d2'], cliff: YOMI_STONE, top: 'ground', bank: ['#2a1c12', '#46301e'] },
  graveyard: { ground: ['#121620', '#20282c', '#2e3a38', '#425046', '#5e6c5a'], style: 'grass', patch: ['#14141c', '#262632', '#3a3a4a', '#545466', '#747488'], patchStyle: 'dirt',
    liquid: ['#08101a', '#122230', '#1e3a4a', '#34596a', '#86b0b8'], cliff: ['#08080e', '#1a1a24', '#2c2c3a', '#424254', '#5e5e74'], top: 'ground', bank: ['#14141a', '#24262c'] },
  peak: { ground: ['#16161e', '#2a2a36', '#40404e', '#5a5a68', '#7c7c8a'], style: 'rock', patch: ['#101a12', '#1e2e1e', '#2e4228', '#425834', '#5e7244'], patchStyle: 'soft',
    liquid: ['#081c2a', '#123852', '#1e5c7c', '#3c8ca8', '#a4dce6'], cliff: ['#0c0c14', '#22222e', '#383846', '#545462', '#7a7a88'], top: 'rock', bank: ['#18181e', '#2c2c36'] },
  village: { ground: ['#2a1e16', '#4a3626', '#6a5038', '#88684a', '#a88662'], style: 'rock', patch: ['#10221a', '#1e3a28', '#2e5234', '#426a42', '#5e8656'], patchStyle: 'soft',
    liquid: ['#081c2a', '#123852', '#1e5c7c', '#3c8ca8', '#a4dce6'], cliff: YOMI_STONE, top: 'rock', bank: ['#2a1c12', '#46301e'] },
};

/** A biome's ground, patch, liquid, cliff and bank colours, for painters outside the hunt map (the region map). */
export function biomeLook(id: string): Pick<Look, 'ground' | 'patch' | 'liquid' | 'cliff' | 'bank' | 'style'> {
  return LOOKS[id] ?? lookFrom(BIOMES[id] ?? BIOMES.snow);
}

/** A biome without a look gets one from its own four-step ramps. */
function lookFrom(b: Biome): Look {
  const five = (r: readonly string[]): Ramp => [mix(r[0], '#000000', 0.45), r[0], r[1], r[2], r[3]];
  return { ground: five(b.ground), style: 'grass', patch: five(b.patch), patchStyle: 'dirt', liquid: [...b.liquid, b.liquid[3]] as unknown as Ramp, cliff: five(b.cliff), top: 'ground', bank: [mix(b.ground[0], '#000000', 0.45), b.ground[0]] };
}

type RGB = [number, number, number];
const rgb = (h: string): RGB => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
function mix(a: string, b: string, t: number): string {
  const x = rgb(a), y = rgb(b);
  return '#' + x.map((v, i) => Math.round(v + (y[i] - v) * t).toString(16).padStart(2, '0')).join('');
}

const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
/** Ordered-dither threshold in [0, 1) for a pixel. */
const bayer = (x: number, y: number) => (BAYER[(y & 3) * 4 + (x & 3)] + 0.5) / 16;

/**
 * The tile grid blurred by two passes of a 15 px box (a soft tent 29 px wide). The grid is
 * constant over each tile and the kernel is narrower than two tiles, so the blur at a pixel is
 * a weighted sum of its tile and the eight around it: TENT[l] holds the weights of the tiles
 * before, at and after a pixel at offset l inside its tile.
 */
const TENT: [number, number, number][] = Array.from({ length: TILE }, (_, l) => {
  const w: [number, number, number] = [0, 0, 0];
  for (let k = -14; k <= 14; k++) w[Math.floor((l + k) / TILE) + 1] += (15 - Math.abs(k)) / 225;
  return w;
});

/** For pixels where mask is set: distance to the nearest unset pixel, in thirds of a pixel (3-4 chamfer). 0 elsewhere. */
function chamfer(mask: Uint8Array, W: number, H: number): Uint16Array {
  const D = new Uint16Array(W * H);
  for (let i = 0; i < W * H; i++) if (mask[i]) D[i] = 60000;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x;
    let v = D[i];
    if (!v) continue;
    let t: number;
    if (x > 0 && (t = D[i - 1] + 3) < v) v = t;
    if (y > 0) {
      if ((t = D[i - W] + 3) < v) v = t;
      if (x > 0 && (t = D[i - W - 1] + 4) < v) v = t;
      if (x < W - 1 && (t = D[i - W + 1] + 4) < v) v = t;
    }
    D[i] = v;
  }
  for (let y = H - 1; y >= 0; y--) for (let x = W - 1; x >= 0; x--) {
    const i = y * W + x;
    let v = D[i];
    if (!v) continue;
    let t: number;
    if (x < W - 1 && (t = D[i + 1] + 3) < v) v = t;
    if (y < H - 1) {
      if ((t = D[i + W] + 3) < v) v = t;
      if (x < W - 1 && (t = D[i + W + 1] + 4) < v) v = t;
      if (x > 0 && (t = D[i + W - 1] + 4) < v) v = t;
    }
    D[i] = v;
  }
  return D;
}

// Small pixel motifs scattered on a jittered grid. Letters: L lighter, D darker, H highlight, S deep shadow.
const TUFTS = [
  ['..L..', 'L.L..', 'L.L.L', 'DDDDD'],
  ['.L..', 'LL.L', 'DDDD'],
  ['...L..', '.L.L..', '.L.L.L', 'DDDDDD'],
];
const DRIFTS = [['.HHH.', 'D...D'], ['.HHHH.', 'D....D'], ['HH.', '..D']];
const RIPPLES_SAND = [['LLL...', '..DDDD'], ['LLLL..', '...DDD'], ['.LLL', 'DD..']];
const PEBBLES = [['.LL.', 'LLDD', '.DD.'], ['LL', 'DS'], ['.L.', 'LDD']];
const CRACKS = [['D...', '.D..', '.DD.', '...D'], ['..D..', '.D...', '.DDD.', '....D'], ['DD...', '..D..', '..DD.', '....D'], ['D..', '.DD']];
const GLINTS = [['..H', '.H.', 'H..'], ['.H', 'H.']];

function motif(seed: number, px: number, py: number, cw: number, ch: number, salt: number, density: number, set: string[][]): string {
  const cx = Math.floor(px / cw), cy = Math.floor(py / ch);
  if (tileRand(seed, cx, cy, salt) >= density) return '';
  const mo = set[Math.floor(tileRand(seed, cx, cy, salt + 1) * set.length)];
  const mh = mo.length, mw = mo[0].length;
  const ox = cx * cw + Math.floor(tileRand(seed, cx, cy, salt + 2) * (cw - mw + 1));
  const oy = cy * ch + Math.floor(tileRand(seed, cx, cy, salt + 3) * (ch - mh + 1));
  const lx = px - ox, ly = py - oy;
  if (lx < 0 || ly < 0 || lx >= mw || ly >= mh) return '';
  const c = mo[ly][lx];
  return c === '.' ? '' : c;
}
const smooth = (t: number) => t * t * (3 - 2 * t);
/** vnoise(seed, px / sx, py / sy) at whole pixels inside the map, from a lattice hashed once up front. */
function lattice(seed: number, sx: number, sy: number, W: number, H: number) {
  const gw = Math.floor((W - 1) / sx) + 2, gh = Math.floor((H - 1) / sy) + 2;
  const v = new Float32Array(gw * gh);
  for (let y = 0; y < gh; y++) for (let x = 0; x < gw; x++) v[y * gw + x] = tileRand(seed, x, y);
  const x0 = new Int32Array(W), fx = new Float32Array(W), y0 = new Int32Array(H), fy = new Float32Array(H);
  for (let x = 0; x < W; x++) { const u = x / sx; x0[x] = Math.floor(u); fx[x] = smooth(u - x0[x]); }
  for (let y = 0; y < H; y++) { const u = y / sy; y0[y] = Math.floor(u); fy[y] = smooth(u - y0[y]); }
  return (px: number, py: number) => {
    const o = y0[py] * gw + x0[px], a = v[o], b = v[o + 1], c = v[o + gw], d = v[o + gw + 1], u = fx[px], t = fy[py];
    return a + (b - a) * u + (c - a) * t + (a - b - c + d) * u * t;
  };
}
const shift = (idx: number, c: string) => Math.max(0, Math.min(4, idx + (c === 'L' ? 1 : c === 'H' ? 2 : c === 'D' ? -1 : c === 'S' ? -2 : 0)));

export function paintTerrain(m: WorldMap): HTMLCanvasElement {
  const W = m.w * TILE, H = m.h * TILE, N = W * H;
  const look = LOOKS[m.biome.id] ?? lookFrom(m.biome);
  const seed = m.seed;

  // 1. Classify pixels: 0 ground, 1 liquid, 2 cliff.
  const tileAt = (tx: number, ty: number) => m.tiles[Math.min(m.h - 1, Math.max(0, ty)) * m.w + Math.min(m.w - 1, Math.max(0, tx))];
  /** The blurred water and cliff grids at a pixel. */
  let bw = 0, bc = 0;
  const blurAt = (x: number, y: number) => {
    const tx = (x / TILE) | 0, ty = (y / TILE) | 0, wx = TENT[x - tx * TILE], wy = TENT[y - ty * TILE];
    bw = 0; bc = 0;
    for (let j = 0; j < 3; j++) for (let k = 0; k < 3; k++) {
      const t = tileAt(tx + k - 1, ty + j - 1), w = wx[k] * wy[j];
      if (t === T_WATER) bw += w; else if (t === T_CLIFF) bc += w;
    }
  };
  const cls = new Uint8Array(N);
  const warpX = lattice(seed + 41, 13, 13, W, H), warpY = lattice(seed + 42, 13, 13, W, H), nudgeN = lattice(seed + 43, 5, 5, W, H);
  for (let py = 0; py < H; py++) for (let px = 0; px < W; px++) {
    const i = py * W + px;
    // Domain warp: look the blurred field up a few pixels away so straight tile edges wobble.
    const wx = Math.min(W - 1, Math.max(0, px + Math.round((warpX(px, py) - 0.5) * 7)));
    const wy = Math.min(H - 1, Math.max(0, py + Math.round((warpY(px, py) - 0.5) * 7)));
    blurAt(wx, wy);
    // The noise that made the tiles decides wherever the blur is undecided (a tile centre is
    // never undecided: the blur there is at least 0.8 for its own class).
    let s = 0;
    if ((bw > 0.12 && bw < 0.78) || (bc > 0.12 && bc < 0.78)) s = sample(m, (px + 0.5) / TILE, (py + 0.5) / TILE);
    const nudge = (nudgeN(px, py) - 0.5) * 0.04;
    if (0.66 * bw + (s === T_WATER ? 0.34 : 0) + nudge > 0.5) cls[i] = 1;
    else if (0.66 * bc + (s === T_CLIFF ? 0.34 : 0) + nudge > 0.5) cls[i] = 2;
  }
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= W || y >= H ? 255 : cls[y * W + x]);

  // 2. Distances.
  const mask = new Uint8Array(N);
  for (let i = 0; i < N; i++) mask[i] = cls[i] === 1 ? 1 : 0;
  const dLiquid = chamfer(mask, W, H); // depth inside liquid
  for (let i = 0; i < N; i++) mask[i] ^= 1;
  const dShore = chamfer(mask, W, H); // distance from liquid
  for (let i = 0; i < N; i++) mask[i] = cls[i] === 2 ? 1 : 0;
  const dCliff = chamfer(mask, W, H); // distance to a cliff's edge
  const down = new Uint8Array(N), up = new Uint8Array(N); // cliff pixels below / above in the column
  const shade = new Uint8Array(N); // ground: rows below a cliff
  const bankUp = new Uint8Array(N); // liquid: rows below a ground edge
  for (let x = 0; x < W; x++) {
    let run = 0;
    for (let y = H - 1; y >= 0; y--) { const i = y * W + x; if (cls[i] === 2) { down[i] = Math.min(255, run); run++; } else run = 0; }
    run = 0;
    let lastCliff = -999, lastGround = -999;
    for (let y = 0; y < H; y++) {
      const i = y * W + x, c = cls[i];
      if (c === 2) { up[i] = Math.min(255, run); run++; lastCliff = y; lastGround = -999; }
      else {
        run = 0;
        if (c === 0) { shade[i] = Math.min(255, y - lastCliff); lastGround = y; }
        else { bankUp[i] = Math.min(255, y - lastGround); lastCliff = -999; }
      }
    }
  }

  // 3. Patches of the second ground material.
  const patchThr = 0.8 - m.biome.patchAmount * 0.45;
  const paving = look.patchStyle === 'paving';
  const patchA = lattice(seed + 7, 120, 100, W, H), patchB = lattice(seed + 8, 26, 22, W, H);
  // Paving: flagstones, one per jittered grid point (the nearest point owns the pixel). Each
  // stone is laid whole: it is there if the patch field is high at its centre.
  const FS = 13, FW = Math.ceil(W / FS) + 2, FH = Math.ceil(H / FS) + 2;
  const fqx = new Float32Array(FW * FH), fqy = new Float32Array(FW * FH), fon = new Uint8Array(FW * FH);
  if (paving) for (let ay = -1; ay < FH - 1; ay++) for (let ax = -1; ax < FW - 1; ax++) {
    const k = (ay + 1) * FW + ax + 1;
    fqx[k] = (ax + 0.15 + tileRand(seed, ax, ay, 65) * 0.7) * FS;
    fqy[k] = (ay + 0.15 + tileRand(seed, ax, ay, 66) * 0.7) * FS;
    fon[k] = vnoise(seed + 7, fqx[k] / 120, fqy[k] / 100) * 0.75 + vnoise(seed + 8, fqx[k] / 26, fqy[k] / 22) * 0.25 > patchThr && tileRand(seed, ax, ay, 62) > 0.07 ? 1 : 0;
  }
  /**
   * The flagstone at a pixel, as a paving shade: 0 none, 1 mortar, 2 lit edge, 3 shaded edge,
   * 4 dark stone, 5 stone, 6 light stone, 7 crack.
   */
  const flag = (px: number, py: number): number => {
    const gx = Math.floor(px / FS), gy = Math.floor(py / FS);
    let any = 0;
    for (let j = 0; j <= 2; j++) for (let k = 0; k <= 2; k++) any |= fon[(gy + j) * FW + gx + k];
    if (!any) return 0;
    let b1 = 1e9, b2 = 1e9, k1 = 0, x1 = 0, y1 = 0, x2 = 0, y2 = 0;
    for (let j = 0; j <= 2; j++) for (let k = 0; k <= 2; k++) {
      const q = (gy + j) * FW + gx + k;
      const qx = fqx[q], qy = fqy[q];
      const dd = (px - qx) * (px - qx) + (py - qy) * (py - qy);
      if (dd < b1) { b2 = b1; x2 = x1; y2 = y1; b1 = dd; k1 = q; x1 = qx; y1 = qy; } else if (dd < b2) { b2 = dd; x2 = qx; y2 = qy; }
    }
    if (!fon[k1]) return 0;
    // Distance to the border with the next stone, and which way that border lies.
    const ex = x2 - x1, ey = y2 - y1, el = Math.hypot(ex, ey) || 1;
    const e = -((px - (x1 + x2) / 2) * ex + (py - (y1 + y2) / 2) * ey) / el;
    const nx = ex / el, ny = ey / el;
    if (e < 1) return 1;
    if (e < 2.2 && (ny < -0.45 || nx < -0.7)) return 2;
    if (e < 2.2 && (ny > 0.45 || nx > 0.7)) return 3;
    if (e > 2 && motif(seed, px, py, 11, 9, 64, 0.25, CRACKS)) return 7;
    const v = tileRand(seed, k1, 0, 63);
    return v < 0.3 ? 4 : v > 0.8 ? 6 : 5;
  };
  // patch: 0 none, 1 the second material (or a paving shade, see flag).
  const patch = new Uint8Array(N);
  for (let py = 0; py < H; py++) for (let px = 0; px < W; px++) {
    const i = py * W + px;
    if (cls[i] === 1) continue;
    if (paving) patch[i] = flag(px, py);
    else {
      const soft = look.patchStyle === 'soft' ? (bayer(px, py) - 0.5) * 0.05 : 0;
      if (patchA(px, py) * 0.75 + patchB(px, py) * 0.25 + soft > patchThr) patch[i] = 1;
    }
  }
  const inPatch = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H && patch[y * W + x] > 0;

  // The ground ramp in half steps (index = 2 x ramp step), so tone areas can be gentle.
  const G: RGB[] = [];
  look.ground.forEach((h, k) => { if (k) G.push(rgb(mix(look.ground[k - 1], h, 0.5))); G.push(rgb(h)); });
  const P = look.patch.map(rgb), PD = rgb(mix(look.patch[2], look.patch[1], 0.5)), PL = rgb(mix(look.patch[2], look.patch[3], 0.45)), L = look.liquid.map(rgb), C = look.cliff.map(rgb), BK = look.bank.map(rgb);
  const base = look.style === 'snow' || look.style === 'sand' ? 6 : 4;
  const PAVE = [P[2], P[1], P[3], PD, PD, P[2], PL, P[1]];

  const toneA = lattice(seed + 5, 96, 72, W, H), toneB = lattice(seed + 6, 26, 22, W, H), toneP = lattice(seed + 9, 30, 24, W, H);
  const crustA = lattice(seed + 51, 12, 9, W, H), crustB = lattice(seed + 52, 4, 4, W, H), iceN = lattice(seed + 56, 40, 30, W, H);
  const strata = lattice(seed + 18, 4, 1, W, H), topN = lattice(seed + 34, 12, 10, W, H);

  /** Plain ground at a pixel: calm tone areas with a few motifs. Returns a ground-ramp index. */
  const groundIdx = (px: number, py: number, plain: boolean): number => {
    // Large, gentle tone areas a half step either side of the base, edged with a pixel of dither.
    const tn = toneA(px, py) * 0.82 + toneB(px, py) * 0.18 + (bayer(px, py) - 0.5) * 0.03;
    const tone = Math.min(8, tn < 0.36 ? base - 1 : tn > 0.68 ? base + 1 : base);
    if (!plain) return tone;
    let c = '';
    switch (look.style) {
      case 'grass': c = motif(seed, px, py, 11, 9, 21, 0.32, TUFTS); break;
      case 'snow': c = motif(seed, px, py, 14, 10, 21, 0.26, DRIFTS); if (!c && tileRand(seed, px, py, 25) < 0.0016) c = 'H'; break;
      case 'sand': c = motif(seed, px, py, 13, 8, 21, 0.3, RIPPLES_SAND); break;
      case 'rock': {
        c = motif(seed, px, py, 12, 10, 21, 0.24, PEBBLES) || motif(seed, px, py, 19, 15, 31, 0.3, CRACKS);
        break;
      }
    }
    return Math.max(0, Math.min(8, tone + (c === 'L' ? 2 : c === 'H' ? 4 : c === 'D' ? -2 : c === 'S' ? -4 : 0)));
  };

  const img = new ImageData(W, H);
  const d = img.data;
  const liquidKind = m.biome.liquidKind;
  for (let py = 0; py < H; py++) for (let px = 0; px < W; px++) {
    const i = py * W + px;
    const c = cls[i];
    const dz = bayer(px, py);
    let col: RGB;
    if (c === 0) {
      const shore = dShore[i] / 3, sh = shade[i];
      const nearEdge = shore <= 3 || sh <= 6;
      if (patch[i]) {
        // Second material.
        if (paving) {
          // Mortar between stones; each stone lit on its upper-left edge, shaded on its lower-right.
          col = PAVE[patch[i]];
        } else {
          const edge = look.patchStyle !== 'soft' && (!inPatch(px - 1, py) || !inPatch(px + 1, py) || !inPatch(px, py - 1) || !inPatch(px, py + 1));
          if (edge) col = P[1];
          else {
            const tn = toneP(px, py) + (dz - 0.5) * 0.04;
            let idx = tn > 0.7 ? 3 : 2;
            if (look.patchStyle === 'dirt') idx = shift(idx, motif(seed, px, py, 9, 8, 71, 0.3, PEBBLES));
            else if (look.patchStyle === 'rock' && motif(seed, px, py, 14, 11, 72, 0.4, CRACKS)) idx = 1;
            col = P[idx];
          }
        }
      } else {
        col = G[groundIdx(px, py, !nearEdge)];
      }
      // Wet edge by the liquid, cast shadow under a cliff face.
      if (shore <= 1) col = liquidKind === 'lava' ? BK[0] : G[Math.max(0, base - 4)];
      else if (shore <= 3 && (shore <= 2 || dz < 0.5) && liquidKind !== 'lava') col = G[Math.max(0, base - 2)];
      if (sh >= 1 && sh <= 4) col = G[Math.max(0, base - 4)];
      else if (sh <= 6 && dz < (7 - sh) / 3) col = G[Math.max(0, base - 2)];
    } else if (c === 1) {
      const dep = dLiquid[i] / 3, bu = bankUp[i];
      if (bu >= 1 && bu <= 3) col = bu === 1 ? BK[1] : BK[0];
      else if (liquidKind === 'lava') {
        // Glowing edges, an orange body, dark crust plates ringed with glow, a few bubbles.
        const crust = crustA(px, py) * 0.8 + crustB(px, py) * 0.2;
        if (bu === 4 || dep <= 1) col = L[4];
        else if (dep <= 3) col = L[3];
        else if (crust > 0.69) col = crust > 0.77 ? L[0] : L[1];
        else if (crust > 0.665) col = L[3];
        else col = motif(seed, px, py, 15, 12, 53, 0.25, [['HH', 'LL'], ['H']]) ? L[4] : L[2];
      } else if (liquidKind === 'ice') {
        if (bu === 4 || dep <= 1) col = L[4];
        else if (dep <= 3) col = L[3];
        else if (motif(seed, px, py, 17, 14, 54, 0.4, CRACKS)) col = L[1];
        else if (motif(seed, px, py, 23, 17, 55, 0.3, GLINTS)) col = L[4];
        else col = iceN(px, py) + (dz - 0.5) * 0.04 > 0.66 ? L[3] : L[2];
      } else {
        // Water and the Styx: a bright line where it meets the bank, shallows, then depth.
        const foam = bu === 4 && tileRand(seed, px >> 1, py, 57) < 0.8;
        if (foam || dep <= 1) col = L[foam ? 4 : 3];
        else if (dep <= 5 || (dep <= 7 && dz < 0.5)) col = L[2];
        else if (dep <= 22 || (dep <= 26 && dz < 0.5)) col = L[1];
        else col = L[0];
        if (dep > 6) {
          const r = motif(seed, px, py, 17, 9, 58, 0.4, [['.LLL.', 'D...D'], ['LLLL'], ['.LLLLL']]);
          if (r === 'L') col = dep <= 22 ? L[3] : L[2];
          else if (r === 'D') col = dep <= 22 ? L[0] : col;
        }
      }
    } else {
      // Cliff: a south face with strata, a rimmed top.
      const dn = down[i], run = dn + up[i] + 1;
      const F = Math.min(11, Math.floor(run * 0.55));
      const e = dCliff[i] / 3;
      if (dn < F) {
        const row = F - 1 - dn;
        const sideL = at(px - 1, py) !== 2, sideR = at(px + 1, py) !== 2;
        if (dn === 0 || sideL || sideR) col = C[0];
        else if (row === 0) col = C[4];
        else {
          let idx = row <= F * 0.45 ? 3 : 2;
          if (row >= F - 3) idx = 1;
          if ((row + Math.floor(tileRand(seed, px >> 3, 0, 17) * 3)) % 4 === 0 && strata(px, py) > 0.38) idx = Math.max(1, idx - 1);
          if (tileRand(seed, px, 0, 19) < 0.07 && row > 1) idx = 1;
          col = C[idx];
        }
      } else if (e <= 1) col = C[0];
      else if (e <= 2.6 && (at(px, py - 2) !== 2 || at(px - 2, py) !== 2 || at(px, py - 3) !== 2)) col = C[4];
      else if (e <= 3.4) col = C[3];
      else if (look.top === 'ground') col = G[groundIdx(px, py, e > 6)];
      else {
        const tn = topN(px, py) + (dz - 0.5) * 0.05;
        col = C[shift(tn < 0.35 ? 2 : 3, motif(seed, px, py, 10, 9, 35, 0.3, PEBBLES))];
      }
    }
    d[i * 4] = col[0]; d[i * 4 + 1] = col[1]; d[i * 4 + 2] = col[2]; d[i * 4 + 3] = 255;
  }
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  cv.getContext('2d')!.putImageData(img, 0, 0);
  return cv;
}

/**
 * Worn paths out of a hunt map to the neighbouring hunts (client/waymarks.ts), painted over the ground: a trodden
 * strip in the ground's own colours (packed snow, bare dirt or scree), ragged at the edge, along tile centres
 * that run out past the map's edge.
 */
export function paintPaths(m: WorldMap, c: HTMLCanvasElement, paths: { x: number; y: number }[][]) {
  const look = LOOKS[m.biome.id] ?? lookFrom(m.biome);
  // Packed snow is greyer and darker than fresh; elsewhere the bare ground of the area's patches shows.
  const ramp = look.style === 'snow' ? [look.ground[1], mix(look.ground[2], look.patch[2], 0.35), look.ground[2]] : [look.patch[1], look.patch[2], look.patch[3]];
  const cols = ramp.map(rgb);
  const x = c.getContext('2d')!;
  const W = c.width, H = c.height;
  for (const path of paths) {
    const pts = path.map((p) => ({ x: p.x * TILE, y: p.y * TILE }));
    const pad = 14;
    const l = Math.max(0, Math.floor(Math.min(...pts.map((p) => p.x)) - pad)), r = Math.min(W, Math.ceil(Math.max(...pts.map((p) => p.x)) + pad));
    const t = Math.max(0, Math.floor(Math.min(...pts.map((p) => p.y)) - pad)), b = Math.min(H, Math.ceil(Math.max(...pts.map((p) => p.y)) + pad));
    if (r <= l || b <= t) continue;
    const img = x.getImageData(l, t, r - l, b - t), d = img.data;
    for (let py = t; py < b; py++) for (let px = l; px < r; px++) {
      // Distance to the path, and how far along it (the far end fades into the trees).
      let best = Infinity;
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1], q = pts[i], vx = q.x - a.x, vy = q.y - a.y, len2 = vx * vx + vy * vy || 1;
        const k = Math.max(0, Math.min(1, ((px - a.x) * vx + (py - a.y) * vy) / len2));
        best = Math.min(best, Math.hypot(px - (a.x + vx * k), py - (a.y + vy * k)));
      }
      const half = 8.5 + (vnoise(m.seed + 91, px / 7, py / 7) - 0.5) * 4;
      if (best > half) continue;
      const u = best / half, dz = bayer(px, py);
      // Ragged edge: the outer third dithers into the ground; the middle is worn lighter in streaks.
      if (u > 0.7 && dz < (u - 0.7) / 0.3) continue;
      const col = u > 0.62 ? cols[0] : vnoise(m.seed + 92, px / 3, py / 9) > 0.62 && dz > 0.4 ? cols[2] : cols[1];
      const o = ((py - t) * (r - l) + (px - l)) * 4;
      d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2];
    }
    x.putImageData(img, l, t);
  }
}
